/**
 * Idea Pad — parent-facing page to propose Maximized.Kids ideas and watch the
 * agent shape them. Two-panel responsive layout (side-by-side ≥960px, stacked
 * below). Workflow lives in portal/IDEA_PAD.md.
 */
import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import {
  submitIdea,
  confirmIdea,
  listIdeas,
  Idea,
  Pillar,
  Tag,
  Priority,
} from '../api/ideas';

const MAX_LEN = 4000;

const PILLAR_CHIP_CLASS: Record<Pillar, string> = {
  Body: 'chip chip-body',
  Brain: 'chip chip-brain',
  Bonds: 'chip chip-bonds',
};

const PRIORITY_BADGE_CLASS: Record<Priority, string> = {
  High: 'priority-badge priority-high',
  Medium: 'priority-badge priority-medium',
  Low: 'priority-badge priority-low',
};

export default function IdeaPadPage() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const [rawText, setRawText] = useState('');
  const [current, setCurrent] = useState<Idea | null>(null);
  const [error, setError] = useState<string | null>(null);

  const historyQuery = useQuery({
    queryKey: ['ideas', user?.sub],
    queryFn: () => listIdeas(user!.idToken),
    enabled: !!user,
  });

  const submit = useMutation({
    mutationFn: (text: string) => submitIdea(user!.idToken, text),
    onSuccess: (idea) => {
      setCurrent(idea);
      setError(null);
      qc.invalidateQueries({ queryKey: ['ideas', user?.sub] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const confirm = useMutation({
    mutationFn: (ideaId: string) => confirmIdea(user!.idToken, ideaId),
    onSuccess: (idea) => {
      setCurrent(idea);
      qc.invalidateQueries({ queryKey: ['ideas', user?.sub] });
    },
    onError: (e: Error) => setError(e.message),
  });

  function handleSubmit() {
    const text = rawText.trim();
    if (!text) return;
    setError(null);
    submit.mutate(text);
  }

  function handleClear() {
    setRawText('');
    setCurrent(null);
    setError(null);
  }

  const charsLeft = MAX_LEN - rawText.length;
  const isShaping = submit.isPending;
  const isConfirming = confirm.isPending;
  const canConfirm =
    !!current && current.status === 'raw' && !isShaping && !isConfirming;

  return (
    <div className="idea-pad-wrap">
      <header className="idea-pad-header">
        <h1>Idea Pad</h1>
        <p className="lede">
          Share an idea for Maximized.Kids. The agent will summarize it,
          classify it under Body, Brain, or Bonds (and other tags), suggest a
          priority, and propose a next step. Review the shaped version, then
          confirm to add it to the suggestion log.
        </p>
      </header>

      <div className="idea-pad-grid">
        {/* -------- Left panel: parent input -------- */}
        <section className="idea-panel idea-input" aria-label="Your idea">
          <div className="idea-panel-head">
            <h2>Your idea</h2>
            <span className="char-count" aria-live="polite">
              {charsLeft} chars left
            </span>
          </div>

          <textarea
            className="idea-textarea"
            placeholder="Type your idea here. Examples: 'Add helmet sensors to the scrimmage video so kids can see hit data after games' or 'Run a quarterly parent night where kids demo their AI projects.'"
            value={rawText}
            onChange={(e) => setRawText(e.target.value.slice(0, MAX_LEN))}
            disabled={isShaping}
            rows={14}
          />

          <div className="idea-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClear}
              disabled={isShaping || (!rawText && !current)}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={isShaping || !rawText.trim()}
            >
              {isShaping ? 'Shaping…' : 'Send suggestion'}
            </button>
          </div>

          {error && <div className="error-banner">{error}</div>}
        </section>

        {/* -------- Right panel: agent output -------- */}
        <section className="idea-panel idea-output" aria-label="Agent output">
          <div className="idea-panel-head">
            <h2>Agent output</h2>
            {current && (
              <span
                className={
                  'status-pill ' +
                  (current.status === 'confirmed'
                    ? 'status-confirmed'
                    : 'status-raw')
                }
              >
                {current.status === 'confirmed' ? 'Confirmed' : 'Pending confirmation'}
              </span>
            )}
          </div>

          {!current && !isShaping && (
            <div className="idea-empty">
              <SparkleIcon />
              <p>
                Your shaped suggestion will appear here once you hit{' '}
                <strong>Send suggestion</strong>.
              </p>
            </div>
          )}

          {isShaping && (
            <div className="idea-loading" role="status">
              <Spinner />
              <p>Shaping your idea…</p>
            </div>
          )}

          {current && !isShaping && <ShapedView idea={current} />}

          {current && current.status === 'raw' && !isShaping && (
            <div className="idea-confirm-row">
              <p className="confirm-hint">
                Looks right? Confirm to log this suggestion. You can also send
                another shape if you'd like to refine the wording first.
              </p>
              <button
                type="button"
                className="btn-primary confirm-btn"
                onClick={() => current && confirm.mutate(current.ideaId)}
                disabled={!canConfirm}
              >
                {isConfirming ? 'Confirming…' : 'Confirm and log'}
              </button>
            </div>
          )}
        </section>
      </div>

      {/* -------- History -------- */}
      <section className="idea-history" aria-label="Recent suggestions">
        <h2>Your recent suggestions</h2>
        {historyQuery.isLoading && <p className="muted">Loading…</p>}
        {historyQuery.isError && (
          <p className="muted">Couldn't load history right now.</p>
        )}
        {historyQuery.data && historyQuery.data.length === 0 && (
          <p className="muted">Nothing here yet — your first suggestion will show up here.</p>
        )}
        {historyQuery.data && historyQuery.data.length > 0 && (
          <ul className="idea-history-list">
            {historyQuery.data.map((idea) => (
              <li key={idea.ideaId} className="idea-history-row">
                <div className="idea-history-main">
                  <span className={PRIORITY_BADGE_CLASS[idea.priority]}>
                    {idea.priority}
                  </span>
                  <span className="idea-history-summary">{idea.summary}</span>
                </div>
                <div className="idea-history-meta">
                  <ChipRow pillars={idea.pillars} tags={idea.tags} compact />
                  <span
                    className={
                      'status-pill ' +
                      (idea.status === 'confirmed'
                        ? 'status-confirmed'
                        : 'status-raw')
                    }
                  >
                    {idea.status === 'confirmed' ? 'Confirmed' : 'Raw'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/* ---------------- Subcomponents ---------------- */

function ShapedView({ idea }: { idea: Idea }) {
  return (
    <div className="shaped">
      {idea.offTopic && (
        <div className="info-banner">
          The agent isn't sure this is strictly about Maximized.Kids — we've
          still saved it so the team can review.
        </div>
      )}

      <Field label="Summary">
        <p className="shaped-summary">{idea.summary}</p>
      </Field>

      <Field label="Classification">
        <ChipRow pillars={idea.pillars} tags={idea.tags} />
      </Field>

      <Field label="Priority">
        <span className={PRIORITY_BADGE_CLASS[idea.priority]}>
          {idea.priority}
        </span>
      </Field>

      {idea.proposedAction && (
        <Field label="Proposed action">
          <p>{idea.proposedAction}</p>
        </Field>
      )}

      {idea.followUp && (
        <Field label="Follow-up">
          <p>{idea.followUp}</p>
        </Field>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="shaped-field">
      <div className="shaped-label">{label}</div>
      <div className="shaped-value">{children}</div>
    </div>
  );
}

function ChipRow({
  pillars,
  tags,
  compact = false,
}: {
  pillars: Pillar[];
  tags: Tag[];
  compact?: boolean;
}) {
  if (pillars.length === 0 && tags.length === 0) {
    return <span className="muted">No classification</span>;
  }
  return (
    <div className={'chip-row' + (compact ? ' chip-row-compact' : '')}>
      {pillars.map((p) => (
        <span key={p} className={PILLAR_CHIP_CLASS[p]}>
          {p}
        </span>
      ))}
      {tags.map((t) => (
        <span key={t} className="chip chip-tag">
          {t}
        </span>
      ))}
    </div>
  );
}

function Spinner() {
  return (
    <svg
      className="spinner"
      width="22"
      height="22"
      viewBox="0 0 50 50"
      aria-hidden="true"
    >
      <circle
        cx="25"
        cy="25"
        r="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="80 40"
      />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
      <circle cx="12" cy="12" r="3.2" />
    </svg>
  );
}
